import { useEffect, useMemo, useRef, useState } from "react";

import Alert from "components/atoms/alert/Alert";
import useLoading from "components/atoms/loading/useLoading";
import useToast from "components/atoms/toast/useToast";

import { mentionRegex } from "utils/mention";
import { getProfileDisplayInfo } from "utils/request/auth";
import { wsUrl } from "utils/urls";
import { uuidv4 } from "utils/uuid";

export function usePDFChatWebSocket({
    reference_uuid,
    user,
    connectionDependencies = [],
    onFileUploaded,
    onFileDeleted,
    onCreateReviewBox,
    onPdfMetadata,
}) {
    const { setToast } = useToast();

    // 로딩 상태
    const { setLoading } = useLoading();

    // 공통 상태
    const wsRef = useRef(null);
    const [threads, setThreads] = useState([]);
    const [selectedThread, setSelectedThread] = useState(null);
    const [profiles, setProfiles] = useState([]);
    const [lastProfileIds, setLastProfileIds] = useState([]);
    const [streamingMessageIds, setStreamingMessageIds] = useState([]);
    const [autoScrollToEnd, setAutoScrollToEnd] = useState(true);
    const [pdfIndexingInfo, setPdfIndexingInfo] = useState({
        total_pages: 1,
        indexed_pages: 0,
        analysis_status: "TODO",
    });
    const pendingMessageRef = useRef(null);

    // 프로필 정보 관리 (공통)
    useEffect(() => {
        if (threads && user && user.profile) {
            // threads 에 참여한 사용자 프로필 이름, 이미지 가져오기
            const profileIdSet = new Set();
            profileIdSet.add(user.profile.id);

            const mentionRegex = /@\[[^\]]+\]\((\d+)\)/g; // @[display](id) 형태
            threads.forEach((thread) => {
                (thread.messages || []).forEach((msg) => {
                    let match;
                    while ((match = mentionRegex.exec(msg.content || "")) !== null) {
                        profileIdSet.add(Number(match[1]));
                    }
                });
            });

            const profile_ids = Array.from(profileIdSet);
            // console.log("profile_ids", profile_ids);
            if (
                profile_ids.length !== lastProfileIds.length ||
                !profile_ids.every((id, idx) => id === lastProfileIds[idx]) // 기존과 다른 profile_id 있는지 확인
            ) {
                setLastProfileIds(profile_ids);
                getProfileDisplayInfo({ profile_ids })
                    .then((res) => {
                        setProfiles(res);
                    })
                    .catch((err) => {
                        console.log("profiles error", err);
                    });
            }
        }
    }, [threads, user, lastProfileIds]);

    // 웹소켓 연결 로직 (connect/disconnect)
    const connectWebSocket = () => {
        if (wsRef.current !== null) return; // 이미 연결되어 있으면 무시
        wsRef.current = new WebSocket(wsUrl("review"));
        wsRef.current.senderId = uuidv4();

        wsRef.current.onopen = () => {
            if (wsRef.current) {
                sendMessage(
                    {
                        action: "init",
                        reference_uuid: reference_uuid,
                    },
                    handleMessage,
                );
            }
        };

        // 메시지를 보낼 때 다시 연결하기 때문에 콘솔 로그만 남김
        wsRef.current.onerror = (error) => {
            console.log(error);
        };

        wsRef.current.onclose = (event) => {
            if (event.code !== 1000) {
                // 정상 종료가 아닌 경우
                console.log(event);
            }
            wsRef.current = null;
        };
    };

    const connectionReady = useMemo(() => connectionDependencies.every(Boolean), [connectionDependencies]);
    useEffect(() => {
        if (connectionReady) {
            connectWebSocket();
        }
        return () => {
            // 웹소켓 연결 종료
            if (wsRef.current) {
                wsRef.current.close();
                wsRef.current = null;
            }
        };
    }, [connectionReady]);

    // 메시지 송신 및 핸들러
    const chatStart = (req_message) => {
        let has_prep_mention = mentionRegex.exec(req_message.content) !== null;

        // 유료 기능 체크
        if (!user.is_subscribed) {
            if (req_message.autoResponse) {
                Alert("info", "유료 기능 알림", "AI 응답은 유료 기능입니다.\nAI 자동 응답 기능을 꺼주세요.");
                return;
            }

            if (has_prep_mention) {
                Alert("info", "유료 기능 알림", "AI 응답은 유료 기능입니다.\n@PREP 멘션을 사용할 수 없습니다.");
                return;
            }
        }

        if (req_message.autoResponse && !has_prep_mention) {
            // AI 자동 응답 선택 시 @PREP 멘션 자동 추가
            req_message.content = "@[PREP](0) " + req_message.content;
        }

        if (wsRef.current === null) {
            // 연결이 끊긴 상태에서 메시지를 보내면 대기 상태로 저장 후 연결되면 전송
            pendingMessageRef.current = req_message;
            connectWebSocket();
            return;
        }

        setLoading(true);
        setAutoScrollToEnd(true);
        sendMessage(
            {
                action: "chat_start",
                thread_id: selectedThread ? selectedThread.id : null,
                review_box_id: req_message.reviewBoxId,
                review_box_color: req_message.reviewBoxColor,
                content: req_message.content,
                new_review_box_data: req_message.newReviewBoxData ? { ...req_message.newReviewBoxData } : null,
            },
            handleMessage,
        );
    };

    const sendMessage = (message_obj, message_handler) => {
        const req_message = {
            ...message_obj,
            sender_id: wsRef.current.senderId,
        };
        // console.log("req_message", req_message);

        if (wsRef.current) {
            wsRef.current.onmessage = message_handler;
            wsRef.current.send(JSON.stringify(req_message));
        }
    };

    const handleMessage = async (event) => {
        setLoading(false);

        const data = JSON.parse(event.data);
        const action = data?.action;
        const payload = data?.payload;
        const sender_id = data?.sender_id;
        const is_my_message = sender_id === wsRef.current.senderId;
        // console.log("handleMessage", data);

        if (!(data && action && payload)) {
            return;
        }
        setAutoScrollToEnd(is_my_message);

        if (action === "init_success") {
            const threads_data = payload.threads_data;
            if (is_my_message) {
                setThreads((prevThreads) => {
                    return [...threads_data];
                });
            }

            if (pendingMessageRef.current) {
                chatStart(pendingMessageRef.current);
                pendingMessageRef.current = null;
            }
        }

        if (action === "update_thread") {
            const new_thread = payload.thread_data;
            updateThreads(new_thread);
            if (is_my_message) {
                setSelectedThread(new_thread);
            }
        }

        if (action === "update_message") {
            updateMessageContent(payload.thread_id, payload.message_id, payload.content);
        }

        if (action === "create_review_box") {
            if (onCreateReviewBox) {
                onCreateReviewBox(payload.review_box_data);
            }
        }

        if (action === "pdf_indexing") {
            setPdfIndexingInfo(payload);
        }

        if (action === "pdf_uploaded") {
            setToast("PDF 파일이 업로드되었습니다.", "info");
            if (onFileUploaded) {
                onFileUploaded();
            }
        }

        if (action === "pdf_deleted") {
            setToast("PDF 파일이 삭제되었습니다.", "info");
            if (onFileDeleted) {
                onFileDeleted();
            }
        }

        if (action === "pdf_metadata") {
            setToast("PDF 파일에서 서지정보를 추출했습니다.", "info");
            if (onPdfMetadata) {
                onPdfMetadata();
            }
        }
    };

    const updateThreads = (new_thread) => {
        setThreads((prevThreads) => {
            const cur_idx = prevThreads.findIndex((x) => x.id === new_thread.id);
            if (cur_idx < 0) {
                return [...prevThreads, new_thread];
            } else {
                return prevThreads.map((thread, idx) => (idx === cur_idx ? new_thread : thread));
            }
        });

        // 스트리밍 메시지 UI 초기화
        setStreamingMessageIds([]);
    };

    const updateMessageContent = (thread_id, message_id, token) => {
        setThreads((prevThreads) => {
            const target_thread = prevThreads.find((x) => x.id === thread_id);
            if (!target_thread) return prevThreads;
            const target_message = target_thread.messages.find((x) => x.id === message_id);
            if (target_message) {
                target_message.content = target_message.content ? target_message.content + token : token;
            }

            if (selectedThread && selectedThread.id === thread_id) {
                setSelectedThread({ ...target_thread });
            }

            setStreamingMessageIds((prev) => (prev.includes(message_id) ? prev : [...prev, message_id]));

            return [...prevThreads];
        });
    };

    // ... 필요에 따라 기타 state/setter 반환
    return {
        threads,
        selectedThread,
        setSelectedThread,
        profiles,
        streamingMessageIds,
        autoScrollToEnd,
        setAutoScrollToEnd,
        pdfIndexingInfo,
        chatStart,
    };
}
